import type { Route } from 'next';

import type { MediaContextPlaybackStatus } from '#cutils/context/MediaContext';
import { getIsMediaOffline } from '#cutils/media/getIsMediaOffline';
import { buildMediaStructuredData } from '#cutils/seo/schema-org/buildModuleMediaStructuredData';
import { resize } from '#utils/image/resize';

import type { MediaCollection } from './ArdPlayer/ArdPlayerInternal';
import type { MediaPlayerMedia, MediaPlayerPosterImage } from './MediaPlayer';
import { MediaPlayer } from './MediaPlayer';

type Props = {
	/**
	 * The copyright information for the media content.
	 */
	copyright?: string | null;
	/**
	 * The duration of the media content.
	 */
	duration: number | undefined;
	/**
	 * Additional meta data for the media content.
	 */
	meta?: string | null | undefined;
	/**
	 * The preload setting for the media player.
	 */
	preload?: string;
	/**
	 * The publication date of the media content.
	 */
	publicationDate?: string | null;
	/**
	 * The alt text for the thumbnail image.
	 */
	thumbnailAltText?: string | null;
	/**
	 * The copyright information for the thumbnail image.
	 */
	thumbnailCopyright?: string | null;
	/**
	 * The link for the thumbnail image.
	 */
	thumbnailLink?: Route | null;
	/**
	 * The URL of the thumbnail image.
	 */
	thumbnailUrl?: string | null;
	/**
	 * The title of the media content.
	 */
	title?: string | null;
	/**
	 * The URL of the media content.
	 */
	url: string;
	/**
	 * Indicates whether the media content is a priority.
	 */
	isPriority?: boolean;
	/**
	 * Indicates whether the media content is a hero.
	 */
	isHero?: boolean;
	/**
	 * Callback function to handle changes in the play status.
	 */
	onPlayStatusChange?(status: MediaContextPlaybackStatus): void;
	/**
	 * The ID of the played content, is needed for the analytics team.
	 */
	rowId: number;
	/**
	 * set to `true` to enable the image zooming effect.
	 */
	enableZoomEffect?: boolean;
	sizes: string;
	playButtonTitle?: string;
};

/**
 * Renders a media player component that can display audio or video content.
 *
 */
export function MediaAudio({
	copyright,
	duration,
	meta = '',
	publicationDate,
	thumbnailAltText,
	thumbnailCopyright,
	thumbnailLink,
	thumbnailUrl,
	title,
	url,
	isPriority,
	isHero,
	onPlayStatusChange,
	enableZoomEffect = false,
	sizes,
	rowId,
	playButtonTitle,
}: Props) {
	let media: MediaPlayerMedia = { type: 'audio' };

	const isMediaOnline = !getIsMediaOffline(meta);

	const posterImage: MediaPlayerPosterImage = {
		sizes,
		altText: thumbnailAltText,
		copyright: thumbnailCopyright,
		enableZoomEffect,
		imageUrl: thumbnailUrl,
		link: thumbnailLink,
		isPriority,
		playButtonTitle,
		isHero,
		showGradient: false,
	};

	if (isMediaOnline && url) {
		const structuredData = buildMediaStructuredData({
			duration,
			publicationDate,
			thumbnail: thumbnailUrl ? resize(thumbnailUrl, 'landscape', 'xl') : undefined,
			title,
			type: 'audio',
			url,
		});

		const mediaCollection: MediaCollection = {
			meta: {
				title: '',
				synopsis: title ?? '',
				clipSourceName: copyright ?? undefined,
				images: [],
				durationSeconds: duration,
			},
			streams: [
				{
					kind: 'main',
					isAudioOnly: true,
					media: [
						{
							url: url,
							mimeType: 'video/mp4',
							audios: [
								{
									kind: 'standard',
									languageCode: 'de',
								},
							],
						},
					],
				},
			],
			pluginData: {
				'trackingPiano@web': {
					avContent: {
						av_content_id: `${rowId}`,
						av_content_type: 'Audio',
						av_player_position: isHero ? 'Header' : 'Body',
					},
				},
			},
		};

		media = {
			mediaCollection,
			structuredData,
			type: 'audio',
		};
	}

	return <MediaPlayer media={media} posterImage={posterImage} rowId={rowId} onPlayStatusChange={onPlayStatusChange} />;
}
